import { FC, useEffect, useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";

import { useAsu, RangeType } from "@framework/provider-asu";
import { useApiCall } from "@framework/react-hooks";
import { TrainDefectSortColumn } from "@framework/types";
import { SortDirection } from "@framework/types-collection";

import { StyledButton } from "../../../../../../components/common/styled/button";
import { DefectsTable } from "./table";
import { filterDefectsByBinding } from "./filter-defects-by-binding";
import { IDefectResponse, IDefectsValues } from "./interfaces";

export interface ITrainDefectsDialogProps {
  open: boolean;
  trainId: number | null;
  onClose: () => void;
  onMaxRateChange?: (maxRate: number | null) => void;
  binding?: { bindObjectType: number; idBindObject: number | null } | null;
}

export const TrainDefectsDialog: FC<ITrainDefectsDialogProps> = props => {
  const { open, trainId, onClose, onMaxRateChange, binding } = props;

  const { startTimestamp, endTimestamp, recorderWindowStart, userSelectedRangeType } = useAsu();

  const [defects, setDefects] = useState<IDefectResponse[]>([]);
  const [sortColumn, setSortColumn] = useState<TrainDefectSortColumn>(TrainDefectSortColumn.Date);
  const [sortOrder, setSortOrder] = useState<SortDirection>(SortDirection.desc);

  const { fn: getDefects, isLoading } = useApiCall(
    (api, { trainId, startTimestamp, endTimestamp, sortColumn, sortOrder }: IDefectsValues) => {
      return api
        .fetchJson({
          url: `/trains/${trainId}/defects`,
          method: "GET",
          data: {
            startTimestamp,
            endTimestamp,
            sortColumn,
            sortOrder,
          },
        })
        .then((json: IDefectResponse[] | null) => json ?? []);
    },
    { success: false, error: false },
  );

  const filteredDefects = useMemo(() => filterDefectsByBinding(defects, binding ?? null), [defects, binding]);

  useEffect(() => {
    if (!open || !trainId || !startTimestamp || !endTimestamp) {
      return;
    }
    if (userSelectedRangeType === RangeType.RECORDER_MODE && !recorderWindowStart) return;

    const values: IDefectsValues = {
      trainId,
      startTimestamp: startTimestamp as string,
      endTimestamp: endTimestamp as string,
      sortColumn,
      sortOrder,
    };

    void getDefects(undefined, values).then(json => {
      if (Array.isArray(json)) {
        setDefects(json);
      } else {
        setDefects([]);
      }
    });
  }, [open, trainId, startTimestamp, endTimestamp, sortColumn, sortOrder]);

  const handleSort = (column: TrainDefectSortColumn) => () => {
    setSortColumn(prev => {
      if (prev === column) {
        setSortOrder(current => (current === SortDirection.asc ? SortDirection.desc : SortDirection.asc));
        return prev;
      }

      setSortOrder(SortDirection.asc);
      return column;
    });
  };

  const maxRate = useMemo(() => {
    if (!filteredDefects.length) return null;
    return filteredDefects.reduce((max, defect) => (defect.Rate > max ? defect.Rate : max), filteredDefects[0].Rate);
  }, [filteredDefects]);

  useEffect(() => {
    if (onMaxRateChange) {
      onMaxRateChange(maxRate);
    }
  }, [maxRate, onMaxRateChange]);

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog fullWidth maxWidth="lg" open={open} onClose={handleClose}>
      <DialogTitle sx={{ fontWeight: 700 }}>
        <FormattedMessage id="pages.scheme.defects.title" />
      </DialogTitle>
      <DialogContent dividers>
        {isLoading ? (
          <Typography variant="body2">
            <FormattedMessage id="form.messages.loading" defaultMessage="Загрузка..." />
          </Typography>
        ) : filteredDefects.length ? (
          <Box sx={{ mt: 1 }}>
            <DefectsTable
              defects={filteredDefects}
              handleSort={handleSort}
              sortColumn={sortColumn}
              sortOrder={sortOrder}
            />
          </Box>
        ) : (
          <Typography variant="body2">
            <FormattedMessage id="pages.scheme.defects.empty" />
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <StyledButton onClick={handleClose} sx={{ minWidth: 80, mb: 1, mr: 2 }}>
          <FormattedMessage id="form.buttons.ok" />
        </StyledButton>
      </DialogActions>
    </Dialog>
  );
};
